import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calculator, Phone, Sparkles, Star } from 'lucide-react';
import { motion } from 'framer-motion';

const FloatingElement = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => (
  <motion.div
    initial={{ y: 0 }}
    animate={{ y: [-10, 10, -10] }}
    transition={{ duration: 4, repeat: Infinity, delay }}
    className="absolute"
  >
    {children}
  </motion.div>
);

export function Hero() {
  return (
    <header className="relative min-h-[100svh] flex items-center">
      <div className="absolute inset-0">
        <motion.div 
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&q=80")',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/80 to-zinc-900/60"></div>
          
          {/* Floating decorative elements */}
          <FloatingElement delay={0}>
            <div className="top-20 left-20 opacity-20">
              <Sparkles className="h-8 w-8 text-yellow-400" />
            </div>
          </FloatingElement>
          
          <FloatingElement delay={1}>
            <div className="top-40 right-32 opacity-30">
              <Star className="h-6 w-6 text-yellow-300" />
            </div>
          </FloatingElement>
          
          <FloatingElement delay={2}>
            <div className="bottom-40 left-32 opacity-25">
              <Sparkles className="h-10 w-10 text-yellow-500" />
            </div>
          </FloatingElement>
        </motion.div>
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-32">
        <div className="max-w-3xl">
          {/* Animated badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="inline-flex items-center gap-2 bg-yellow-400/10 border border-yellow-400/20 rounded-full px-4 py-2 mb-6"
          >
            <Sparkles className="h-4 w-4 text-yellow-400" />
            <span className="text-yellow-400 text-sm font-medium">Premium Construction Services</span>
          </motion.div>
          
          <motion.h1 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-light leading-tight text-zinc-100 mb-6 md:mb-8 relative"
          >
            <span className="relative">
              Premier Construction
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: "100%" }}
                transition={{ duration: 1.5, delay: 1.5 }}
                className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-yellow-400 to-yellow-600"
              />
            </span>
            <br className="hidden sm:block" /> 
            in Westchester & Fairfield
          </motion.h1>
          
          <motion.p 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="text-base sm:text-lg text-zinc-400 mb-8 md:mb-12 leading-relaxed max-w-2xl"
          >
            Leading renovation company serving Westchester County and Fairfield County. 
            Delivering excellence in kitchen renovations, bathroom remodeling, and home improvements 
            with unmatched quality and craftsmanship.
          </motion.p>

          <p className="text-sm text-zinc-300 mb-8">Licensed and insured in New York and Connecticut</p>
          
          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <a
                href="tel:9149606433"
                className="group inline-flex items-center justify-center gap-4 px-8 py-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-zinc-900 hover:from-yellow-500 hover:to-yellow-600 transition-all duration-300 shadow-lg hover:shadow-xl relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <Phone className="h-5 w-5 relative z-10" />
                <span className="text-sm tracking-wider font-medium relative z-10">(914) 960-6433</span>
              </a>
            </motion.div>
            
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link 
                to="/consultation" 
                className="group inline-flex items-center justify-center gap-4 px-8 py-4 bg-zinc-800/50 backdrop-blur-sm border border-yellow-400/20 text-yellow-400 hover:bg-yellow-400 hover:text-zinc-900 transition-all duration-300 relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-yellow-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                <span className="text-sm tracking-wider relative z-10">Free Consultation</span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 relative z-10" />
              </Link>
            </motion.div>
            
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/project-planner"
                className="group inline-flex items-center justify-center gap-4 px-8 py-4 bg-zinc-800/50 backdrop-blur-sm border border-zinc-700 text-zinc-400 hover:text-yellow-400 hover:border-yellow-400 transition-all duration-300"
              >
                <Calculator className="h-4 w-4" />
                <span className="text-sm tracking-wider">Start Your Project</span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
      
      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-yellow-400/50 rounded-full flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1 h-3 bg-yellow-400 rounded-full mt-2"
          />
        </motion.div>
      </motion.div>
    </header>
  );
}